/**
 * Move a requisition through its lifecycle — the recruiting analogue of the incident
 * `TransitionControls`, and the same rule: **the illegal moves have no DOM node.**
 *
 * `allowedTransitions(status)` is the whole list, mapped once. There is no loop over
 * the six statuses computing a `disabled` flag, because a disabled attribute is a
 * rendering decision an operator removes in devtools, and a greyed control asserts
 * "the system will not let you" about a move that exists for nobody. `filled`,
 * `cancelled` and `closed` are terminal — their allowed set is `[]` — so a finished
 * requisition renders no controls and one sentence, not a dead toolbar.
 *
 * The table lives in `web/src/recruiting/requisition.ts`, transcribed from the
 * server's `src/modules/recruiting/transitions.ts`. It cannot be the authority: a
 * stale screen can still issue a move the server refuses `409
 * WORKFORCE_INVALID_STATE_TRANSITION`, and that refusal is rendered — the server's
 * own sentence through `ApiErrorNote`, and the server's own `allowed_transitions[]`
 * printed beside it rather than re-derived from this client's copy.
 */

import type { ReactNode } from 'react';

import { useI18n } from '../../i18n';
import type { WorkforceApiError } from '../../api/errors';
import { allowedTransitionsFromRefusal } from '../../api/useApiMutation';
import {
  allowedTransitions,
  isTerminalStatus,
  STATUS_LABEL,
  TRANSITION_LABEL,
  type RequisitionStatus,
} from '../../recruiting/requisition';
import { ApiErrorNote } from '../../states/ApiErrorNote';
import { StatusNote } from '../../states/StatusNote';

export interface RequisitionStatusControlsProps {
  readonly status: RequisitionStatus;
  readonly pending: boolean;
  readonly error: WorkforceApiError | null;
  readonly onMove: (to: RequisitionStatus) => void;
}

export function RequisitionStatusControls({
  status,
  pending,
  error,
  onMove,
}: RequisitionStatusControlsProps): ReactNode {
  const { t } = useI18n();
  const moves = allowedTransitions(status);
  const serverAllowed = error === null ? [] : allowedTransitionsFromRefusal(error);

  return (
    <section
      className="wp-section"
      aria-labelledby="w1-transition-heading"
      data-testid="w1-transitions"
    >
      <h3 className="wp-section__title" id="w1-transition-heading">
        {t('recruiting.jobs.transition.title')}
      </h3>

      {isTerminalStatus(status) ? (
        <StatusNote
          severity="info"
          title={t('recruiting.jobs.transition.terminal.title')}
          testId="w1-transition-terminal"
        >
          <p>{t('recruiting.jobs.transition.terminal.body')}</p>
        </StatusNote>
      ) : (
        <>
          <div className="wp-toggle-group" data-testid="w1-transition-buttons">
            {moves.map((move) => (
              <button
                key={move}
                className="wp-button"
                type="button"
                data-testid={`w1-move-${move}`}
                disabled={pending}
                onClick={() => {
                  onMove(move);
                }}
              >
                {t(TRANSITION_LABEL[move])}
              </button>
            ))}
          </div>
          <p className="wp-small wp-muted" data-testid="w1-transition-note">
            {t('recruiting.jobs.transition.derived', {
              status: t(STATUS_LABEL[status]),
              count: moves.length,
            })}
          </p>
        </>
      )}

      {error === null ? null : (
        <>
          <ApiErrorNote
            error={error}
            subject={t('recruiting.jobs.subjectWord')}
            testId="w1-transition-error"
          />
          {serverAllowed.length === 0 ? null : (
            <p className="wp-small" data-testid="w1-transition-server-allowed">
              {t('recruiting.jobs.transition.serverAllowed', { list: serverAllowed.join(', ') })}
            </p>
          )}
        </>
      )}
    </section>
  );
}
